import type { ReactNode } from 'react';
import { useMemo } from 'react';
import useMonarchStore from '@/stores/monarchStore';
import { computeComparisonPeriods, formatDateRange } from '@/lib/dateComparison';

interface PageHeaderProps {
  title: string;
  subtitle: string;
  badges?: ReactNode;
}

export default function PageHeader({ title, subtitle, badges }: PageHeaderProps) {
  const dateRange = useMonarchStore((s) => s.ui.dateRange);
  const start = dateRange.start instanceof Date ? dateRange.start : new Date(dateRange.start);
  const end = dateRange.end instanceof Date ? dateRange.end : new Date(dateRange.end);

  const periodLabel = useMemo(
    () => formatDateRange(start, end),
    [start, end],
  );

  const comparison = useMemo(
    () => computeComparisonPeriods(start, end),
    [start, end],
  );

  return (
    <div className="flex items-center justify-between flex-wrap gap-4">
      <div>
        <h1 className="font-display text-2xl text-text mb-1">{title}</h1>
        <p className="text-xs text-text-3">
          {subtitle} | {periodLabel}
        </p>
        {dateRange.compareEnabled && (
          <div className="flex items-center gap-2 mt-1.5">
            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium bg-bg-2 border border-border text-text-3">
              PoP: {formatDateRange(comparison.pop.start, comparison.pop.end)}
            </span>
            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium bg-bg-2 border border-border text-text-3">
              YoY: {formatDateRange(comparison.yoy.start, comparison.yoy.end)}
            </span>
          </div>
        )}
      </div>
      {badges && <div className="flex items-center gap-3">{badges}</div>}
    </div>
  );
}
